import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { submitFeedback } from "@/lib/api";

const STORAGE_PREFIX = "sg_overall_fb::";

export default function OverallFeedback({ caseId, problemType }) {
  const [vote, setVote] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    const v = localStorage.getItem(`${STORAGE_PREFIX}${caseId}::vote`);
    if (v === "up" || v === "down") setVote(v);
  }, [caseId]);

  const setVoteAndPersist = async (next) => {
    if (!caseId) return;
    const toggled = vote === next ? null : next;
    setVote(toggled);
    localStorage.setItem(`${STORAGE_PREFIX}${caseId}::vote`, toggled || "");
    try {
      await submitFeedback({
        case_id: caseId,
        item_type: "overall",
        item_id: "overall",
        item_text: "overall",
        theme_label: "",
        problem_type: problemType || "",
        vote: toggled,
      });
    } catch (_) {}
  };

  const sendComment = async () => {
    const text = comment.trim();
    if (!text || !caseId) return;
    setSubmitting(true);
    try {
      await submitFeedback({
        case_id: caseId,
        item_type: "overall",
        item_id: `overall-comment-${Date.now()}`,
        item_text: "overall_comment",
        theme_label: "",
        problem_type: problemType || "",
        vote: vote,
        comment: text,
      });
      setSent(true);
      setComment("");
      setTimeout(() => setSent(false), 3000);
    } catch (_) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#12181A] p-6 sm:p-8"
      data-testid="overall-feedback"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            How did we do?
          </div>
          <div className="mt-2 text-white text-lg font-medium">
            Was this research plan useful?
          </div>
          <div className="mt-1 text-sm text-[#A0AAB2]">
            One tap, no signup needed.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setVoteAndPersist("up")}
            aria-pressed={vote === "up"}
            data-testid="overall-feedback-up"
            className={`h-11 w-11 inline-flex items-center justify-center rounded-full border transition-[background-color,border-color,color] ${
              vote === "up"
                ? "bg-[#35D07F] border-[#35D07F] text-black"
                : "bg-transparent border-white/15 text-white/70 hover:text-white hover:border-white/30"
            }`}
          >
            <ThumbsUp className="h-5 w-5" strokeWidth={vote === "up" ? 2.5 : 2} />
          </button>
          <button
            type="button"
            onClick={() => setVoteAndPersist("down")}
            aria-pressed={vote === "down"}
            data-testid="overall-feedback-down"
            className={`h-11 w-11 inline-flex items-center justify-center rounded-full border transition-[background-color,border-color,color] ${
              vote === "down"
                ? "bg-[#FF5252] border-[#FF5252] text-black"
                : "bg-transparent border-white/15 text-white/70 hover:text-white hover:border-white/30"
            }`}
          >
            <ThumbsDown className="h-5 w-5" strokeWidth={vote === "down" ? 2.5 : 2} />
          </button>
        </div>
      </div>

      <div className="mt-5">
        <label className="text-xs text-white/60" htmlFor="overall-comment">
          What did we miss, or what would you change?
        </label>
        <Textarea
          id="overall-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          data-testid="overall-feedback-comment"
          placeholder="Optional — we read every note."
          className="mt-2 min-h-[96px] bg-[#0B0F10] border-white/10 text-white placeholder:text-white/25 focus-visible:ring-[#35D07F]/50 rounded-xl text-[15px] leading-relaxed"
        />
        <div className="mt-3 flex items-center gap-3">
          <Button
            onClick={sendComment}
            disabled={submitting || !comment.trim()}
            data-testid="overall-feedback-submit"
            className="rounded-full bg-white/5 hover:bg-white/10 text-white border border-white/15 h-10 px-5 disabled:opacity-40"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Sending…
              </>
            ) : (
              "Send feedback"
            )}
          </Button>
          {sent && (
            <div
              className="inline-flex items-center gap-1.5 text-xs text-[#35D07F]"
              data-testid="overall-feedback-sent"
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
              Thanks — we've got it.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
